/**
 * Takvimde gezinilebilir aralık.
 *
 * Tarih şeridi 14 günlük bir LİSTE değil, kaydırılabilir bir PENCEREdir:
 * ekranda hep 14 gün durur, ama pencere geçmişe ve geleceğe iki yıl boyunca
 * kayabilir. Önceki hâlde aralık `[bugün, bugün+13]` olarak sabitti ve
 * kullanıcı 14 günden ötesini hiçbir şekilde göremiyordu — geçmiş bir dersin
 * yoklamasına bakmak ya da üç hafta sonrasını planlamak imkânsızdı.
 *
 * Sınırların varlık sebebi teknik değil, ürünsel: sonsuz bir aralık "kaydırdım
 * kaydırdım, hiçbir şey yok" deneyimi üretir ve kullanıcı ne kadar ileri
 * gidebileceğini asla öğrenemez. Uçta devre dışı kalan bir buton bunu tek
 * bakışta söyler.
 */

import { utcToZonedDateString } from '@shared/time';

/** Stüdyonun fiziksel saat dilimi — hafta başlangıcı buna göre hesaplanır. */
export const STUDIO_TIME_ZONE = 'Europe/Istanbul';

/** Şeritte aynı anda görünen gün sayısı. 7'nin katı olmalı: pencere hafta
 *  başına hizalandığı için ileri/geri adımı tam hafta atlamalıdır. */
export const VISIBLE_DAYS = 14;

export const CALENDAR_PAST_DAYS = 730; // ~2 yıl geriye
export const CALENDAR_FUTURE_DAYS = 730; // ~2 yıl ileriye

/**
 * Takvim gününe gün ekler/çıkarır.
 *
 * Öğlen (12:00Z) üzerinden hesaplanır: gece yarısından yürütmek, yaz saati
 * geçişlerinde bir günü atlatabilir veya tekrarlatabilir.
 *
 * Bu fonksiyonun üç ayrı kopyası vardı (`DateStrip`, `uiStore` ve bu dosya
 * yazılmadan önce `WeeklyGrid`'in dolaylı kullanımı); üçü de aynı işi yapıyordu
 * ama biri düzeltilse diğerleri sessizce eski davranışta kalırdı.
 */
export function addDays(dateStr: string, days: number): string {
  const shifted = new Date(`${dateStr}T12:00:00Z`);
  shifted.setUTCDate(shifted.getUTCDate() + days);
  return shifted.toISOString().slice(0, 10);
}

/**
 * Bugünün takvim günü — STÜDYO saat dilimine göre.
 *
 * `toISOString().slice(0, 10)` UTC gününü verir: İstanbul'da 00:30'da
 * (21:30Z) açılan takvim "bugün"ü bir gün geride gösteriyor, "bugüne dön"
 * düne gidiyordu. Tarayıcının saat dilimi de kullanılmıyor — yurt dışından
 * bakan üye stüdyonun gününü görmeli.
 */
export function todayISO(now: Date = new Date()): string {
  return utcToZonedDateString(now, STUDIO_TIME_ZONE);
}

export interface CalendarBounds {
  min: string;
  max: string;
}

export function calendarBounds(today: string = todayISO()): CalendarBounds {
  return {
    min: addDays(today, -CALENDAR_PAST_DAYS),
    max: addDays(today, CALENDAR_FUTURE_DAYS),
  };
}

/**
 * Tarihi gezinilebilir aralığa sıkıştırır.
 *
 * Sınır denetimi arayüzde DEĞİL burada: butonlar uçta devre dışı kalsa bile
 * `setSelectedDate` başka yerlerden de çağrılıyor (bekleme listesi bildirimi,
 * ödeme dönüşü, derin bağlantı). Tek bir kapıda sıkıştırmak, aralık dışına
 * çıkmayı kaynağı ne olursa olsun imkânsız kılar.
 *
 * ISO tarih dizeleri sabit genişlikte olduğu için sözlüksel karşılaştırma
 * kronolojik karşılaştırmaya eşittir; `Date` nesnesi kurmaya gerek yok.
 */
export function clampToCalendarRange(date: string, today: string = todayISO()): string {
  const { min, max } = calendarBounds(today);
  if (date < min) return min;
  if (date > max) return max;
  return date;
}
